
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export const useUserManagement = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: 'superadmin' | 'member' }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ role })
        .eq('id', userId);
      
      if (error) throw error;
    },
    onSuccess: (_, { userId, role }) => {
      // Update the user in all member queries instead of invalidating
      queryClient.setQueriesData(
        { queryKey: ['members'] },
        (oldData: Array<{ id: string; email: string; first_name: string; last_name: string; role: string }> | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((user) => 
            user.id === userId ? { ...user, role } : user
          );
        }
      );
      toast.success('User role updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update user role');
      console.error('Error:', error);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId }
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      return data;
    },
    onSuccess: (_, userId) => {
      // Remove the user from all member queries instead of invalidating
      queryClient.setQueriesData(
        { queryKey: ['members'] },
        (oldData: Array<{ id: string; email: string; first_name: string; last_name: string; role: string }> | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((user) => user.id !== userId);
        }
      );
      toast.success('User completely removed from system');
    },
    onError: (error) => {
      toast.error('Failed to delete user');
      console.error('Error:', error);
    },
  });

  const handleImpersonation = async (userId: string, userName: string) => {
    try {
      const { error, shouldRedirect } = await startImpersonation(userId);
      
      if (error) {
        toast.error(`Failed to start impersonation: ${error}`);
      } else if (shouldRedirect) {
        toast.success(`Now impersonating ${userName}`);
        navigate('/');
      }
    } catch (error) {
      toast.error('Failed to start impersonation');
      console.error('Impersonation error:', error);
    }
  };

  return {
    updateRoleMutation,
    deleteUserMutation,
    handleImpersonation
  };
};
